import React from 'react'
import type { HistoricalReport } from '../../../shared/domain/viewableReports/types.no-deps'
import type { CommonDeps } from '../base/CommonDeps'
import { usePromiseManager } from '../util/PromiseManager'
import { OriginatingReports } from './OriginatingReports'
import { ReportHistoryExpandingButton } from './ReportHistoryExpandingButton'

interface ExpandedContentProps {
    renderReport: (report: HistoricalReport) => React.ReactNode
    reports: HistoricalReport[]
}

function ExpandedContent(props: ExpandedContentProps) {
    return <OriginatingReports renderReport={props.renderReport} reports={props.reports} />
}

interface Props {
    deps: CommonDeps
    /** The number of reports we expect to display when requested. (It might be different if the visibility of the reports has changed) */
    expectedReportCount: number
    renderReport: (reportData: HistoricalReport) => React.ReactNode
    rootClassName?: React.HTMLAttributes<HTMLDivElement>['className']
    fetchReports: () => Promise<HistoricalReport[]>
    expandImmediately?: boolean
}

/**
 * A collapsed 'History' button which when clicked causes the component to fetch the details of the reports,
 * and expand to show a tile for each report.  Each tile can be clicked to expand that report.
 *
 * storybook: file://./../../../../stories/portal/ReportHistory.stories.tsx
 *
 * https://www.figma.com/file/0abUs0eJtrAq3aeYRdCoGi/Actions?node-id=4345%3A200024&mode=dev
 */
export function OriginatingReportHistory(props: Props) {
    if (props.expectedReportCount === 0) return null

    return <ExpandingButton {...props} />
}

function ExpandingButton(props: Props) {
    const [isExpanded, setIsExpanded] = React.useState(props.expandImmediately)
    const dataPromiseManager = usePromiseManager<HistoricalReport[]>(props.deps)
    const [isDataRequested, setIsDataRequested] = React.useState(props.expandImmediately)

    const toggleExpansion = () => {
        if (!isExpanded && !isDataRequested) {
            dataPromiseManager.trigger(() => props.fetchReports())
            setIsDataRequested(true)
        }
        setIsExpanded((expanded) => !expanded)
    }

    React.useEffect(() => {
        let cleanUp: () => void = null
        if (isExpanded && isDataRequested) {
            dataPromiseManager.trigger(() => props.fetchReports())
            cleanUp = () => dataPromiseManager.cancel()
        } else {
            setIsDataRequested(false)
        }

        return cleanUp
    }, [props.fetchReports])

    const itemCount = dataPromiseManager.maybeGet() ? dataPromiseManager.maybeGet().length : props.expectedReportCount

    // set a min-height for the 'pulse' spinner to occupy, so that content doesn't 'jump' when the data loads
    const containerStyle: React.CSSProperties = dataPromiseManager.isInProgress()
        ? { display: 'flex', alignItems: 'center', minHeight: 7.4 * props.expectedReportCount + 'rem' }
        : {}

    return (
        <ReportHistoryExpandingButton
            expectedReportCount={itemCount}
            isExpanded={isExpanded}
            rootClassName={props.rootClassName}
            toggleExpansion={toggleExpansion}
        >
            <div style={containerStyle}>
                {dataPromiseManager.render((reports) => (
                    <ExpandedContent reports={reports} renderReport={props.renderReport} />
                ))}
            </div>
        </ReportHistoryExpandingButton>
    )
}
